import type { BusinessSubscription, BusinessInvitation } from "lens-platform-sdk";
import { isActiveSeat, isCanceledSeat } from "src/services/getSubscriptionSeatState";
import { isPendingSubscriptionInvitation } from "src/services/getInvitationState";

interface SubscriptionSeatCount {
  /**
   * Total number of seats in the subscription (=`BusinessSubscription["seats"]`)
   */
  total: BusinessSubscription["seats"];
  /**
   * `total` - (`active` + `canceled` + `pending`)
   */
  available: number,
  /**
   * `active` + `canceled` + `pending`
   */
  unavailable: number,
  /**
   * The number of seats that is a used seat (in `BusinessSubscription["usedSeats"]`)
   * and with `deactivatedAt !== null` condition.
   */
  canceled: number,
  /**
   * The number of seats that is a used seat (in `BusinessSubscription["usedSeats"]`)
   * and with `deactivatedAt === null` condition.
   */
  active: number,
  /**
   * The number of invitations includes this subscription and with `pending` status
   */
  pending: number,
}

/**
 * Get seats count of a subscription
 *
 * @returns a `SubscriptionSelectOption` object @see {@link SubscriptionSeatCount}
 */
export const getSubscriptionSeatCount = (
  businessInvitations: BusinessInvitation[],
  businessSubscriptions?: BusinessSubscription | Array<BusinessSubscription>
) => {
  const subscriptionSeatCount: SubscriptionSeatCount = (
    Array.isArray(businessSubscriptions)
      ? businessSubscriptions
      : [businessSubscriptions]
  ).reduce((accumulator, subscription) => {
    // Invitations that contain this businessSubscription.id
    const businessInvitationsBySubscriptionId = businessInvitations.filter(
      (invitations) => invitations.subscriptionId === subscription?.id
    );
    const total = subscription?.seats ?? 0;
    const canceled = subscription?.usedSeats.filter(isCanceledSeat).length ?? 0;
    const usedSeats = subscription?.usedSeats.length ?? 0;
    const pending = businessInvitationsBySubscriptionId.filter(isPendingSubscriptionInvitation).length;
    const active = subscription?.usedSeats.filter(isActiveSeat).length ?? 0;

    accumulator.total += total;
    accumulator.available += (total - usedSeats - pending);
    accumulator.unavailable += usedSeats + pending;
    accumulator.canceled += canceled;
    accumulator.active += active;
    accumulator.pending += pending;

    return accumulator;
  }, {
    total: 0,
    available: 0,
    unavailable: 0,
    canceled: 0,
    active: 0,
    pending: 0
  });

  return subscriptionSeatCount;
};
